/**
 * MentionTextArea — a textarea with the exact chat ``@``-mention experience.
 *
 * Extracted from CommandRail so surfaces outside the chat rail (e.g. the
 * table-artefact composer) get the identical 3-stage picker: type
 * (Section/Table) → source file → section title / table caption, with the
 * same breadcrumb, keyboard grammar (↑/↓ move, Enter/Tab pick, Esc close,
 * Backspace-on-empty-query steps back a stage, Backspace after a token
 * deletes it atomically), attachment chips, and MentionMenu overlay.
 *
 * Controlled on ``value`` + ``attachments`` (lifted to the parent); the
 * picker's transient state (stage, files, assets, highlight) is owned here.
 * The mention model itself is the shared pure code in ``../mentions``.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getDocuments, type LibraryAssetItem, listLibraryAssets } from "../../api/client";
import type { MentionAttachment } from "../../types";
import {
  attachmentKey,
  clearMentionQuery,
  compareAlphanumeric,
  deleteTokenAtCaret,
  detectMentionTrigger,
  insertMentionToken,
  type MentionStage,
  nextHighlightedIndex,
  removeTokenByNumber,
  renumberMentions,
  resolveMentionSpan,
} from "../mentions";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";
import { getCaretLeft } from "./caretCoordinates";
import MentionMenu, { type MentionMenuEntry } from "./MentionMenu";

/** Stage-1 options of the @-mention picker (mirrors CommandRail). */
const MENTION_TYPE_OPTIONS: ReadonlyArray<{ key: "section" | "table"; label: string; sublabel: string }> = [
  { key: "section", label: "Section", sublabel: "attach a source document section" },
  { key: "table", label: "Table", sublabel: "attach a source document table" },
];

interface MentionPickState {
  stage: MentionStage;
  triggerIndex: number;
  caretEnd: number;
  query: string;
  assetType?: "section" | "table";
  fileId?: string;
  fileName?: string;
}

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column" },
  mentionChipRow: { display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "6px" },
  mentionChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 8px",
    borderRadius: "999px",
    background: brandColors.light,
    border: `1px solid ${surfaceColors.lineStrong}`,
    fontSize: "10px",
    color: inkColors.ink2,
    maxWidth: "100%",
  },
  mentionChipBadge: {
    fontFamily: fonts.mono,
    fontWeight: 700,
    fontSize: "9px",
    color: brandColors.primary,
    flexShrink: 0,
  },
  mentionChipKind: {
    fontFamily: fonts.mono,
    fontWeight: 700,
    fontSize: "9px",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    color: brandColors.primary,
    flexShrink: 0,
  },
  mentionChipLabel: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "220px" },
  mentionChipDismiss: {
    flexShrink: 0,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: inkColors.ink4,
    padding: "0 2px",
    fontSize: "10px",
    lineHeight: 1,
    ":hover": { color: inkColors.ink2 },
  },
  composerWrap: { position: "relative", display: "flex", flexDirection: "column" },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    resize: "vertical",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "4px",
    padding: "9px 10px",
    fontSize: "12px",
    fontFamily: fonts.body,
    color: inkColors.ink,
    outline: "none",
    lineHeight: 1.5,
    ":focus": { border: `1px solid ${brandColors.primary}` },
  },
});

export interface MentionTextAreaProps {
  libraryId: string | null;
  value: string;
  onChange: (value: string) => void;
  attachments: MentionAttachment[];
  onAttachmentsChange: (attachments: MentionAttachment[]) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
  testId?: string;
}

export const MentionTextArea: React.FC<MentionTextAreaProps> = ({
  libraryId,
  value,
  onChange,
  attachments,
  onAttachmentsChange,
  placeholder,
  disabled = false,
  minHeight = 120,
  testId,
}) => {
  const styles = useStyles();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  // Card anchoring + click-outside: composerWrapRef is the positioned ancestor
  // the card measures against; mentionMenuRef is the card itself.
  const composerWrapRef = useRef<HTMLDivElement | null>(null);
  const mentionMenuRef = useRef<HTMLDivElement | null>(null);
  const [mentionLeft, setMentionLeft] = useState(0);

  const [mentionState, setMentionState] = useState<MentionPickState | null>(null);
  const [mentionHighlight, setMentionHighlight] = useState(-1);
  const [mentionFiles, setMentionFiles] = useState<{ id: string; name: string }[] | null>(null);
  const [mentionAssets, setMentionAssets] = useState<LibraryAssetItem[] | null>(null);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionError, setMentionError] = useState<string | null>(null);
  const [pendingCaret, setPendingCaret] = useState<number | null>(null);

  // Apply a programmatic caret move after a token insert/delete/stage change.
  useEffect(() => {
    if (pendingCaret == null) return;
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(pendingCaret, pendingCaret);
    }
    setPendingCaret(null);
  }, [pendingCaret]);

  const mentionStage = mentionState?.stage ?? null;
  const mentionAssetType = mentionState?.assetType ?? null;
  const mentionFileId = mentionState?.fileId ?? null;

  // Fetch the current stage's options (file list / asset list). The type
  // stage is static, so it needs no fetch.
  useEffect(() => {
    if (!mentionStage || mentionStage === "type" || !libraryId) return;
    setMentionLoading(true);
    setMentionError(null);
    const fail = (err: unknown): void => {
      setMentionError(err instanceof Error ? err.message : String(err));
      setMentionLoading(false);
    };
    if (mentionStage === "file") {
      getDocuments(libraryId).then((docs) => {
        setMentionFiles(docs.map((d) => ({ id: d.id, name: d.name })));
        setMentionLoading(false);
      }, fail);
    } else if (mentionAssetType) {
      listLibraryAssets(libraryId, mentionAssetType, { fileId: mentionFileId }).then((res) => {
        setMentionAssets(res.assets);
        setMentionLoading(false);
      }, fail);
    }
  }, [mentionStage, mentionAssetType, mentionFileId, libraryId]);

  const mentionQuery = mentionState?.query ?? "";
  const filteredTypes = useMemo(
    () => MENTION_TYPE_OPTIONS.filter((t) => t.label.toLowerCase().includes(mentionQuery.trim().toLowerCase())),
    [mentionQuery],
  );
  const filteredFiles = useMemo(
    () =>
      (mentionFiles ?? [])
        .filter((f) => f.name.toLowerCase().includes(mentionQuery.trim().toLowerCase()))
        .sort((a, b) => compareAlphanumeric(a.name, b.name)),
    [mentionFiles, mentionQuery],
  );
  const filteredAssets = useMemo(
    () =>
      (mentionAssets ?? [])
        .filter((a) => a.title.toLowerCase().includes(mentionQuery.trim().toLowerCase()))
        .sort((a, b) => compareAlphanumeric(a.title, b.title)),
    [mentionAssets, mentionQuery],
  );

  const mentionEntries: MentionMenuEntry[] = useMemo(() => {
    if (!mentionState) return [];
    if (mentionState.stage === "type") {
      return filteredTypes.map((t) => ({ key: t.key, label: t.label, sublabel: t.sublabel }));
    }
    if (mentionState.stage === "file") {
      return filteredFiles.map((f) => ({ key: f.id, label: f.name }));
    }
    return filteredAssets.map((a, i) => ({
      key: `${a.file_id}:${i}:${a.title}`,
      label: a.title,
      sublabel: a.file_name || undefined,
    }));
  }, [mentionState, filteredTypes, filteredFiles, filteredAssets]);

  // First item pre-highlighted whenever the list changes (autocomplete UX).
  useEffect(() => {
    setMentionHighlight(mentionEntries.length > 0 ? 0 : -1);
  }, [mentionEntries]);

  // Anchor the compact card horizontally over the "@" column.
  useEffect(() => {
    if (!mentionState) return;
    const el = inputRef.current;
    const wrap = composerWrapRef.current;
    if (!el || !wrap) return;
    const x =
      el.getBoundingClientRect().left - wrap.getBoundingClientRect().left + getCaretLeft(el, mentionState.triggerIndex);
    const max = Math.max(0, wrap.clientWidth - 320);
    setMentionLeft(Math.max(0, Math.min(x, max)));
  }, [mentionState, value]);

  // Click-outside dismissal: mousedown outside both the card and the textarea.
  useEffect(() => {
    if (!mentionState) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (mentionMenuRef.current?.contains(target)) return;
      if (inputRef.current?.contains(target)) return;
      setMentionState(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mentionState]);

  const handleMentionSelect = useCallback(
    (idx: number) => {
      if (!mentionState) return;
      // The picker persists after the trigger pattern breaks, so recompute a
      // safe insertion span from the live caret rather than the stored one.
      const liveCaret = inputRef.current?.selectionStart ?? mentionState.caretEnd;
      const span = resolveMentionSpan(value, liveCaret, mentionState.triggerIndex);
      if (mentionState.stage === "type") {
        const picked = filteredTypes[idx];
        if (!picked) return;
        const { next, caret } = clearMentionQuery(value, span.triggerIndex, span.caretEnd);
        onChange(next);
        setPendingCaret(caret);
        setMentionAssets(null);
        setMentionState({
          ...mentionState,
          stage: "file",
          assetType: picked.key,
          triggerIndex: span.triggerIndex,
          caretEnd: caret,
          query: "",
        });
        return;
      }
      if (mentionState.stage === "file") {
        const picked = filteredFiles[idx];
        if (!picked) return;
        const { next, caret } = clearMentionQuery(value, span.triggerIndex, span.caretEnd);
        onChange(next);
        setPendingCaret(caret);
        setMentionState({
          ...mentionState,
          stage: "asset",
          fileId: picked.id,
          fileName: picked.name,
          triggerIndex: span.triggerIndex,
          caretEnd: caret,
          query: "",
        });
        return;
      }
      const picked = filteredAssets[idx];
      if (!picked || !mentionState.assetType) return;
      const attachment: MentionAttachment = {
        assetType: mentionState.assetType,
        fileId: picked.file_id || mentionState.fileId || "",
        fileName: picked.file_name || mentionState.fileName || "",
        label: picked.title,
      };
      // Re-picking an already-attached source is a no-op (keeps 1 marker : 1
      // attachment). Otherwise append, insert a marker numbered as the new
      // index+1, then renumber markers + array to text order.
      if (attachments.some((a) => attachmentKey(a) === attachmentKey(attachment))) {
        setMentionState(null);
        return;
      }
      const nextAttachments = [...attachments, attachment];
      const inserted = insertMentionToken(value, span.triggerIndex, span.caretEnd, nextAttachments.length);
      const r = renumberMentions(inserted.next, nextAttachments);
      onChange(r.value);
      setPendingCaret(inserted.caret);
      onAttachmentsChange(r.attachments);
      setMentionState(null);
    },
    [mentionState, filteredTypes, filteredFiles, filteredAssets, value, onChange, attachments, onAttachmentsChange],
  );

  // Backspace on an empty query steps back one stage (asset → file → type).
  const handleMentionBack = useCallback(() => {
    setMentionState((st) => {
      if (!st) return st;
      if (st.stage === "asset") return { ...st, stage: "file", fileId: undefined, fileName: undefined };
      if (st.stage === "file") return { ...st, stage: "type", assetType: undefined };
      return st;
    });
  }, []);

  const handleRemoveAttachment = useCallback(
    (attachment: MentionAttachment) => {
      const idx = attachments.findIndex((a) => attachmentKey(a) === attachmentKey(attachment));
      if (idx < 0) return;
      // Pass the FULL array — renumberMentions drops the stripped marker's
      // now-orphaned attachment (mapping is by marker number).
      const stripped = removeTokenByNumber(value, idx + 1);
      const r = renumberMentions(stripped, attachments);
      onChange(r.value);
      onAttachmentsChange(r.attachments);
    },
    [value, onChange, attachments, onAttachmentsChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Escape always closes an open picker.
      if (mentionState && e.key === "Escape") {
        e.preventDefault();
        setMentionState(null);
        return;
      }
      // The picker persists after the "@query" pattern breaks, so its keyboard
      // grammar only captures keys while the caret is inside the live trigger;
      // otherwise the card stays visible but inert and keys fall through.
      const liveCaret = inputRef.current?.selectionStart ?? null;
      const liveTrigger = mentionState && liveCaret != null ? detectMentionTrigger(value, liveCaret) : null;
      const mentionTriggerLive = !!liveTrigger && liveTrigger.triggerIndex === mentionState?.triggerIndex;
      if (mentionState && mentionTriggerLive) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionHighlight((idx) => nextHighlightedIndex(idx < 0 ? -1 : idx, 1, mentionEntries.length));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionHighlight((idx) =>
            nextHighlightedIndex(idx < 0 ? mentionEntries.length : idx, -1, mentionEntries.length),
          );
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          // Swallow even with an empty list — Enter mid-pick must never
          // insert a newline with a dangling "@query".
          e.preventDefault();
          if (mentionHighlight >= 0) handleMentionSelect(mentionHighlight);
          return;
        }
        if (e.key === "Backspace" && mentionState.query === "" && mentionState.stage !== "type") {
          e.preventDefault();
          handleMentionBack();
          return;
        }
      }
      // Atomic marker delete: Backspace right after an "@[n]" removes it + its
      // attachment, then renumbers survivors.
      if ((!mentionState || !mentionTriggerLive) && e.key === "Backspace") {
        const el = inputRef.current;
        if (el && el.selectionStart != null && el.selectionStart === el.selectionEnd) {
          const removed = deleteTokenAtCaret(value, el.selectionStart);
          if (removed) {
            e.preventDefault();
            const r = renumberMentions(removed.next, attachments);
            onChange(r.value);
            setPendingCaret(removed.caret);
            onAttachmentsChange(r.attachments);
          }
        }
      }
    },
    [
      mentionState,
      mentionEntries,
      mentionHighlight,
      handleMentionSelect,
      handleMentionBack,
      value,
      onChange,
      attachments,
      onAttachmentsChange,
    ],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const next = e.target.value;
      const caret = e.target.selectionStart ?? next.length;
      const trigger = detectMentionTrigger(next, caret);
      if (trigger) {
        setMentionState((prev) =>
          prev && prev.triggerIndex === trigger.triggerIndex
            ? { ...prev, query: trigger.query, caretEnd: caret }
            : { stage: "type", triggerIndex: trigger.triggerIndex, caretEnd: caret, query: trigger.query },
        );
      } else {
        // Persist while the "@" anchor survives; close only when it's deleted
        // (alongside select / click-outside / Escape).
        setMentionState((prev) => (prev && next[prev.triggerIndex] !== "@" ? null : prev));
      }
      // Reconcile + renumber in one pass; only nudge the caret when the marker
      // set actually changed (guards ordinary typing).
      const r = renumberMentions(next, attachments);
      onChange(r.value);
      if (r.changed) setPendingCaret(caret);
      onAttachmentsChange(r.attachments);
    },
    [onChange, attachments, onAttachmentsChange],
  );

  return (
    <div className={styles.root}>
      {attachments.length > 0 && (
        <div className={styles.mentionChipRow}>
          {attachments.map((a, i) => (
            <span
              key={attachmentKey(a)}
              className={styles.mentionChip}
              title={a.fileName ? `${a.label} — ${a.fileName}` : a.label}
            >
              <span className={styles.mentionChipBadge}>{`[${i + 1}]`}</span>
              <span className={styles.mentionChipKind}>{a.assetType === "table" ? "Table" : "Section"}</span>
              <span className={styles.mentionChipLabel}>{a.label}</span>
              <button
                type="button"
                className={styles.mentionChipDismiss}
                onClick={() => handleRemoveAttachment(a)}
                aria-label={`Remove attached ${a.assetType} ${a.label}`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <div className={styles.composerWrap} ref={composerWrapRef}>
        {mentionState && (
          <MentionMenu
            ref={mentionMenuRef}
            style={{ left: mentionLeft, right: "auto" }}
            stage={mentionState.stage}
            breadcrumb={[
              ...(mentionState.assetType ? [mentionState.assetType === "table" ? "Table" : "Section"] : []),
              ...(mentionState.fileName ? [mentionState.fileName] : []),
            ]}
            entries={mentionEntries}
            highlightedIndex={mentionHighlight}
            loading={mentionLoading && mentionState.stage !== "type"}
            error={mentionError}
            disabledHint={libraryId ? null : "Select a library to mention its sections and tables."}
            onSelect={handleMentionSelect}
            onClose={() => setMentionState(null)}
            onHoverItem={setMentionHighlight}
          />
        )}
        {/* The textarea paints its own text; a picked attachment stays visible
            as its literal ``@[n]`` token, named by the chip row above. */}
        <textarea
          ref={inputRef}
          className={styles.textarea}
          style={{ minHeight }}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-label="Mention input"
          data-testid={testId}
        />
      </div>
    </div>
  );
};

export default MentionTextArea;
